import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import { useIntl } from 'react-intl'
import { useOnlineStatus } from 'hooks/useOnlineStatus'
import { useAppSelector } from 'hooks/reduxHooks'
import usePrevious from 'hooks/usePrevious'
import GeoContext from 'contexts/geoContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { reportLog } from 'services/userMonitoring'
import type {
  HotelSearchResultsData,
  ExperienceSearchResultsData,
  HotelSearchErrorData,
  ExperienceSearchErrorData,
  ChatUiActionData,
  ManualItineraryDeleteRequest,
  ManualItineraryMoveRequest,
} from 'tripPlanner/api/types'
import { useManualItineraryDeleteMutation, useManualItineraryMoveMutation } from 'tripPlanner/hooks/api/chat'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { resetPendingExperienceDaySelections, syncPendingExperienceDaySelection } from 'tripPlanner/utils/pendingExperienceDaySelection'
import { buildOfferIdContext, buildRoomOptionContext, resolveOccupancyAgeThresholdsForSendMessage } from 'tripPlanner/assistant/utils/messageEnrichment'
import { getAllOffers } from 'selectors/offerSelectors'
import type { AssistantOfferPageTurnAuthority } from 'tripPlanner/assistant/api/types'
import { useAssistantSurfaceSnapshot, type AssistantSurfaceSnapshotBuildResult } from 'tripPlanner/assistant/surfaceSnapshots/useAssistantSurfaceSnapshot'
import { buildTripPlannerSnapshot } from 'tripPlanner/assistant/surfaceSnapshots/buildTripPlannerSnapshot'
import { buildTripPlannerDegradedIdentity } from 'tripPlanner/assistant/surfaceSnapshots/tripPlannerSnapshotIdentity'
import { buildAssistantIdentityScopeKey } from 'tripPlanner/assistant/utils/assistantIdentityScopeKey'
import { useSurfaceActivityAccumulator } from './useSurfaceActivityAccumulator'
import type { UserData } from '../types'
import { useChatCore } from 'tripPlanner/assistant/runtime/chatStream'
import { useItineraryUpdates } from '../hooks/useItineraryUpdates'
import { useTripChatCartReconciliation } from '../hooks/useTripChatCartReconciliation'
import type { AnonymousClaimTransfer } from 'tripPlanner/assistant/hooks/useAnonymousChatClaiming'
import { useTripPlannerEventBus, useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { ChatActionsContext, type ChatActionsValue } from './ChatActionsContext'
import { ChatStreamStatusContext, type ChatStreamStatusValue } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { ChatScrollContext, type ChatScrollValue } from './ChatScrollContext'
import { ChatUiActionContext, type ChatUiActionValue } from './ChatUiActionContext'
import { ChatStreamMessagesContext, type ChatStreamMessagesValue } from './ChatStreamMessagesContext'
import { ItineraryContext, type ItineraryContextValue } from './ItineraryContext'
import { ChatFilesContext, type ChatFilesValue } from './ChatFilesContext'
import { StaffRewindContext, useStaffRewindValue } from 'tripPlanner/assistant/contexts/StaffRewindContext'
import { AddedItineraryItemContext, type AddedItineraryItemContextValue } from 'tripPlanner/assistant/contexts/AddedItineraryItemContext'
import { dismissPendingTripPlannerSnackbar } from 'tripPlanner/components/Snackbar/TripPlannerSnackbarHost'
import { useTurnAuthoritySnapshot } from './TurnAuthoritySnapshotContext'

export interface TripPlannerChatProviderProps {
  chatId: number | undefined
  setChatIdForStreaming: (id: number | undefined) => void
  prompt: string | undefined
  isDevMode: boolean
  /** Staff-only "rewind": true only when viewing another user's trip. Shows per-turn
   * inspect/fork affordances and allows itinerary time-travel. */
  canStaffRewind: boolean
  /** Navigate to a chat by id — used by staff "rewind" fork to open the new chat. */
  navigateToChat: (chatId: number) => void
  isLoggedIn: boolean
  orderIds?: Array<string>
  refetchChatList: () => Promise<unknown>
  waitForAnonymousChatClaim: (signal?: AbortSignal) => Promise<void>
  startChatTitleGeneration: (chatId: number, message: string) => void
  updateChatLastActivityTimestamp: (chatId: number, iso: string) => void
  updateChatMetadata: (chatId: number, name: string, updatedAt: string) => void
  userData: UserData
  chatKey: string
  anonymousClaimTransfer: AnonymousClaimTransfer | undefined
  children: React.ReactNode
}

export function TripPlannerChatProvider({ children, ...props }: TripPlannerChatProviderProps) {
  const eventBus = useTripPlannerEventBus()
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const store = useStore<App.State>()
  const [files, setFiles] = useState<Array<File>>([])
  const { waitForAnonymousChatClaim } = props

  const isStreamingRef = useRef(false)
  const activeAssistantMessageIdRef = useRef<string | undefined>(undefined)

  const isOnline = useOnlineStatus()

  // ── Wire streaming callbacks ──

  const onHotelSearchResults = useCallback((data: HotelSearchResultsData, generation: number) => {
    eventBus.dispatch({ type: 'hotel_search_results', data, generation })
  }, [eventBus])

  const onExperienceSearchResults = useCallback((data: ExperienceSearchResultsData, generation: number) => {
    eventBus.dispatch({ type: 'experience_search_results', data, generation })
  }, [eventBus])

  const onHotelSearchError = useCallback((data: HotelSearchErrorData, generation: number) => {
    eventBus.dispatch({ type: 'hotel_search_error', data, generation })
  }, [eventBus])

  const onExperienceSearchError = useCallback((data: ExperienceSearchErrorData, generation: number) => {
    eventBus.dispatch({ type: 'experience_search_error', data, generation })
  }, [eventBus])

  // Buffer UI actions in state so they survive the landing→chat transition.
  // MapContainer may not be mounted when the first ui_action arrives (its event
  // subscription isn't active yet). The context-based buffer lets MapContainer
  // pick up the pending action on mount.
  const [pendingUiAction, setPendingUiAction] = useState<ChatUiActionData | undefined>(undefined)

  const clearPendingUiAction = useCallback(() => {
    setPendingUiAction(undefined)
  }, [])

  const onUiAction = useCallback((data: ChatUiActionData) => {
    setPendingUiAction(data)
    eventBus.dispatch({ type: 'ui_action', data })
  }, [eventBus])

  // ── Itinerary domain ──

  const {
    itineraryData,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    addedItemLink,
    handleItineraryUpdates,
    handleEnrichmentComplete: handleItineraryEnrichmentComplete,
    handleRefetchItinerary,
    refetchCurrentChatItinerary,
  } = useItineraryUpdates({ chatId: props.chatId, isStreamingRef, activeAssistantMessageIdRef })

  // ── Assistant surface snapshot (read tool) ──

  // TripPlannerChatProvider is the producer of ItineraryContext, not a descendant -- calling
  // useItineraryContext() here would throw, so resolveTurnAuthority below closes over the
  // local itineraryData instead. Calling useAssistantSurfaceSnapshot() directly (not
  // useAssistantSurface()) is safe -- each hook call gets independent useRef-backed caches --
  // and is the right layer, since useAssistantSurface bundles chat-history/claiming machinery
  // this provider already owns its own version of via useChatCore.
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const turnAuthoritySnapshotRef = useTurnAuthoritySnapshot()
  const { resolveSurfaceSnapshotAuthority, clearAssistantSurfaceSnapshotRefs } = useAssistantSurfaceSnapshot('trip_planner')
  const { drainSurfaceActivity, restoreDrainedActivity, clearSurfaceActivity } = useSurfaceActivityAccumulator()

  // Bypassing useAssistantSurface() also bypasses its auth-scope-change cache clear, so it is
  // replicated directly here -- real for Trip Chat via the anonymous-to-member claim flow
  // (anonymousClaimTransfer/waitForAnonymousChatClaim above), the same class of transition
  // useAssistantSurface protects against for offer page. clearSurfaceActivity is cleared here
  // too, for the same reason: this transition doesn't dispatch reset_chat_state (that only
  // covers chat switch), so it's the only path that would otherwise miss it.
  const authScope = buildAssistantIdentityScopeKey(memberId)
  const previousAuthScope = usePrevious(authScope, authScope)
  useEffect(() => {
    if (previousAuthScope !== authScope) {
      clearAssistantSurfaceSnapshotRefs()
      clearSurfaceActivity()
    }
  }, [authScope, previousAuthScope, clearAssistantSurfaceSnapshotRefs, clearSurfaceActivity])

  const resolveTurnAuthority = useCallback(async(
    chatId: number,
    accessToken: string | undefined,
    signal?: AbortSignal,
  ): Promise<AssistantOfferPageTurnAuthority | undefined> => {
    // Drained before the try block so it survives even a synchronous buildTripPlannerSnapshot
    // throw -- the drained entries are held in this call's own local variable, not a shared
    // ref, so a second, overlapping resolveTurnAuthority call (submit/regenerate racing before
    // the disabled-button state commits) can never clobber this call's restore target.
    const drainedActivity = drainSurfaceActivity()

    let snapshot: AssistantSurfaceSnapshotBuildResult | undefined
    try {
      snapshot = buildTripPlannerSnapshot({
        chatId,
        turnAuthoritySnapshot: turnAuthoritySnapshotRef.current,
        surfaceActivityEntries: drainedActivity,
      })
    } catch(error) {
      // Building the snapshot is the one step here that can throw synchronously
      // (unlike resolveSurfaceSnapshotAuthority, which always resolves) -- mirrors
      // OfferPageAssistantPanel's own resolveTurnAuthority exactly.
      reportLog('[TripPlannerChatProvider] Failed to build trip_planner snapshot; falling back to degraded identity', { error })
      snapshot = undefined
    }

    const resolvedAuthority = await resolveSurfaceSnapshotAuthority(chatId, snapshot, accessToken, signal)
    if (resolvedAuthority.kind === 'snapshot_ref') {
      // Nothing to do on success: the drained entries are already gone from the live buffer,
      // and that's correct -- they were reported. Only a non-success outcome needs to act.
      return { snapshotRef: resolvedAuthority.snapshotRef }
    }

    restoreDrainedActivity(drainedActivity)
    // itineraryData feeds tripId derivation here, not the snapshot builder above -- kept as
    // a real dep (below) even though buildTripPlannerSnapshot itself doesn't need it, so this
    // callback still gets recreated on every itinerary change, but for a genuine reason.
    return { degradedSurfaceIdentity: buildTripPlannerDegradedIdentity({ chatId, itineraryData }) }
  }, [itineraryData, turnAuthoritySnapshotRef, resolveSurfaceSnapshotAuthority, drainSurfaceActivity, restoreDrainedActivity])

  // ── Chat core ──

  const messagesWrapperRef = useRef<HTMLDivElement | null>(null)

  const {
    messages,
    filteredMessages,
    userFeedbackMap,
    setUserFeedbackMap,
    isChatError,
    chatError,
    loadingState,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    submitMessage,
    regenerateResponse,
    stopStreaming,
    retryLastMessage,
    handleScroll,
    isAtBottom,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    setFollowTail,
    dynamicSpacerHeight,
    resetConversation,
    resetStreamingState,
    streamError,
    isStalled,
    reasoningSummaries,
    toolCallInfo,
    latestGenerationRef,
  } = useChatCore({
    chatId: props.chatId,
    setChatId: props.setChatIdForStreaming,
    prompt: props.prompt,
    isDevMode: props.isDevMode,
    files,
    setFiles,
    isLoggedIn: props.isLoggedIn,
    orderIds: props.orderIds,
    refetchChatList: props.refetchChatList,
    startChatTitleGeneration: props.startChatTitleGeneration,
    updateChatLastActivityTimestamp: props.updateChatLastActivityTimestamp,
    updateChatMetadata: props.updateChatMetadata,
    onItineraryUpdates: handleItineraryUpdates,
    onEnrichmentComplete: handleItineraryEnrichmentComplete,
    onStreamComplete: handleRefetchItinerary,
    onHotelSearchResults,
    onExperienceSearchResults,
    onHotelSearchError,
    onExperienceSearchError,
    onUiAction,
    messagesWrapperRef,
    userData: props.userData,
    surface: 'trip_planner',
    chatKey: props.chatKey,
    resolveTurnAuthority,
  })

  // Sync isStreamingRef after render but before paint — useLayoutEffect eliminates the
  // 1-tick gap that would allow refetchOnWindowFocus to fire during streaming.
  useLayoutEffect(() => {
    isStreamingRef.current = isStreaming
  }, [isStreaming])

  // Itinerary updates arrive mid-turn, so the newest assistant message is the one that
  // caused them. Kept in a ref because useItineraryUpdates runs before the chat core.
  useLayoutEffect(() => {
    activeAssistantMessageIdRef.current = messages.findLast(message => message.role === 'assistant')?.id
  }, [messages])

  // An "Adding to trip" snackbar waits for the itinerary update that replaces it. If the turn
  // ends without one — the assistant asked a question instead of adding — retire it here
  // rather than leaving it spinning until its safety timeout.
  const wasStreamingRef = useRef(false)
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      dismissPendingTripPlannerSnackbar()
    }
    wasStreamingRef.current = isStreaming
  }, [isStreaming])

  // Single reset handler — placed after useChatCore so latestGenerationRef is in scope
  // Note: enrichment state (enrichingStopIds/enrichingItemIds) is derived from itinerary data
  // and auto-resets when chatId changes, so no manual reset is needed.
  useEventSubscription('reset_chat_state', () => {
    clearScrollTarget()
    resetPendingExperienceDaySelections()
    latestGenerationRef.current = 0
    setPendingUiAction(undefined)
    setFiles([])
  })

  // ── Chat message senders ──

  const sendMessage = useCallback(async(message: string, options?: SendMessageOptions) => {
    syncPendingExperienceDaySelection(options)

    let enrichedMessage = options?.offerId
      ? `${message}${buildOfferIdContext(options.offerId, options.offerType)}`
      : message

    if (options?.roomOption) {
      enrichedMessage = `${enrichedMessage}${buildRoomOptionContext(options.roomOption, {
        ageThresholds: resolveOccupancyAgeThresholdsForSendMessage(options, getAllOffers(store.getState())),
      })}`
    }

    return submitMessage(enrichedMessage)
  }, [submitMessage, store])

  const sendPostAuthMessage = useCallback(async(signal?: AbortSignal) => {
    await waitForAnonymousChatClaim(signal)
    // System-generated follow-up, not a new user-authored message — bypasses
    // the spoof-send confirmation (see useStreamConnection.handleSubmit).
    return submitMessage('I have logged in.', { bypassSpoofConfirm: true })
  }, [submitMessage, waitForAnonymousChatClaim])

  // ── Memoized context values ──

  const actionsValue = useMemo<ChatActionsValue>(() => ({
    submitMessage,
    sendMessage,
    sendPostAuthMessage,
    regenerateResponse,
    stopStreaming,
    resetConversation,
    resetStreamingState,
    setFollowTail,
    retryLastMessage,
  }), [submitMessage, sendMessage, sendPostAuthMessage, regenerateResponse, stopStreaming, resetConversation, resetStreamingState, setFollowTail, retryLastMessage])

  const statusValue = useMemo<ChatStreamStatusValue>(() => ({
    chatId: props.chatId,
    chatKey: props.chatKey,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    loadingState,
    streamError,
    isStalled,
    isOnline,
    reasoningSummaries,
    toolCallInfo,
  }), [props.chatId, props.chatKey, isStreaming, isStreamOpen, isAwaitingMessageEnd, loadingState, streamError, isStalled, isOnline, reasoningSummaries, toolCallInfo])

  const scrollValue = useMemo<ChatScrollValue>(() => ({
    handleScroll,
    isAtBottom,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    dynamicSpacerHeight,
    messagesWrapperRef,
  }), [handleScroll, isAtBottom, hasContentOutOfView, enableFollowAndScrollToBottom, dynamicSpacerHeight, messagesWrapperRef])

  const uiActionValue = useMemo<ChatUiActionValue>(() => ({
    pendingUiAction,
    clearPendingUiAction,
  }), [pendingUiAction, clearPendingUiAction])

  const viewAddedItem = useCallback(() => {
    if (!addedItemLink) return
    eventBus.dispatch({
      type: 'view_itinerary_item',
      stopId: addedItemLink.stopId,
      dayId: addedItemLink.dayId,
      itemId: addedItemLink.itemId,
    })
  }, [addedItemLink, eventBus])

  const addedItineraryItemValue = useMemo<AddedItineraryItemContextValue>(() => ({
    addedItemLink,
    viewAddedItem,
  }), [addedItemLink, viewAddedItem])

  const streamMessagesValue = useMemo<ChatStreamMessagesValue>(() => ({
    messages,
    filteredMessages,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
  }), [messages, filteredMessages, isChatError, chatError, userFeedbackMap, setUserFeedbackMap])

  // Staff-only rewind. When a snapshot is being inspected it replaces the live itinerary so
  // the whole panel time-travels; nothing is mutated and live data returns on exit.
  // The finalized assistant turns (oldest → newest) are the states the stepper walks —
  // this must match the turns MessagesList renders per-message rewind controls on.
  const assistantMessageIds = useMemo(
    () => filteredMessages.filter(message => message.role === 'assistant' && message.isFinalized).map(message => message.id),
    [filteredMessages],
  )
  const staffRewind = useStaffRewindValue({
    chatId: props.chatId,
    region: currentRegionCode || props.userData.region || 'AU',
    canStaffRewind: props.canStaffRewind,
    assistantMessageIds,
    navigateToChat: props.navigateToChat,
  })
  const effectiveItineraryData = staffRewind.snapshotItinerary ?? itineraryData

  const hasItineraryData = Boolean(effectiveItineraryData?.stops?.length)

  const multiCartId = effectiveItineraryData?.multiCartId

  const { mutateAsync: deleteManualItineraryTargetMutation } = useManualItineraryDeleteMutation()
  const { mutateAsync: moveManualItineraryItemsMutation } = useManualItineraryMoveMutation()
  const [isManualItineraryDeletePending, setIsManualItineraryDeletePending] = useState(false)
  const isManualItineraryDeletePendingRef = useRef(false)
  const [isManualItineraryMovePending, setIsManualItineraryMovePending] = useState(false)
  const isManualItineraryMovePendingRef = useRef(false)
  const [isFlightCartMutationPending, setFlightCartMutationPending] = useState(false)

  useTripChatCartReconciliation({
    chatId: props.chatId,
    itineraryData,
    isLoggedIn: props.isLoggedIn,
    isStreaming,
    isStreamOpen,
    isManualItineraryDeletePending,
    isManualItineraryMovePending,
    isFlightCartMutationPending,
    waitForAnonymousChatClaim,
    anonymousClaimTransfer: props.anonymousClaimTransfer,
  })

  const deleteManualItineraryTarget = useCallback(async(target: ManualItineraryDeleteRequest) => {
    if (isStreaming || isStreamOpen || isManualItineraryDeletePendingRef.current || isManualItineraryMovePendingRef.current) return

    if (!props.chatId) {
      showSnackbar(intl.formatMessage({
        id: 'f5L_AqJEDPR7oRx_l8lK',
        defaultMessage: 'Unable to delete from itinerary. Please try again.',
        description: 'Error snackbar after manually deleting an item, stop, or day from a trip planner itinerary fails',
      }), 'critical')
      return
    }

    isManualItineraryDeletePendingRef.current = true
    setIsManualItineraryDeletePending(true)

    try {
      const result = await deleteManualItineraryTargetMutation({
        chatId: props.chatId,
        region: currentRegionCode || props.userData.region || 'AU',
        target,
      })
      if (result.conversationContextSynced) {
        showSnackbar(intl.formatMessage({
          id: 'TRmwtLCL6KmMMN1QxBg6',
          defaultMessage: 'Removed from itinerary',
          description: 'Success snackbar after manually deleting an item, stop, or day from a trip planner itinerary',
        }), 'success')
      } else {
        showSnackbar(intl.formatMessage({
          id: '_pHdot57rf1emFxKUA-B',
          defaultMessage: 'Removed from itinerary, but AI context could not sync. Please refresh before continuing.',
          description: 'Critical snackbar after a manual itinerary delete succeeds but the AI conversation context update fails',
        }), 'critical')
      }
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'f5L_AqJEDPR7oRx_l8lK',
        defaultMessage: 'Unable to delete from itinerary. Please try again.',
        description: 'Error snackbar after manually deleting an item, stop, or day from a trip planner itinerary fails',
      }), 'critical')
    } finally {
      isManualItineraryDeletePendingRef.current = false
      setIsManualItineraryDeletePending(false)
    }
  }, [isStreaming, isStreamOpen, props.chatId, props.userData.region, currentRegionCode, deleteManualItineraryTargetMutation, intl])

  const moveManualItineraryItems = useCallback(async(request: ManualItineraryMoveRequest) => {
    const moveCount = request.moves.length
    if (isStreaming || isStreamOpen || isManualItineraryDeletePendingRef.current || isManualItineraryMovePendingRef.current) return false

    if (!props.chatId) {
      showSnackbar(intl.formatMessage({
        id: 'niZexKiXSQvQU6UwoSLi',
        defaultMessage: 'Unable to move itinerary item. Please try again.',
        description: 'Error snackbar after manually moving an item to another day in a trip planner itinerary fails',
      }), 'critical')
      return false
    }

    isManualItineraryMovePendingRef.current = true
    setIsManualItineraryMovePending(true)

    try {
      const result = await moveManualItineraryItemsMutation({
        chatId: props.chatId,
        region: currentRegionCode || props.userData.region || 'AU',
        request,
      })

      if (!result.success) {
        showSnackbar(intl.formatMessage({
          id: '44SsvAemYy2YP1WNK08K',
          defaultMessage: 'Some itinerary items could not be moved. Please try again.',
          description: 'Error snackbar after one or more manually moved trip planner itinerary items fail',
        }), 'critical')
        return false
      }

      if (result.conversationContextSynced) {
        showSnackbar(intl.formatMessage(
          {
            id: '0S0Cl33sBE6rr06ad_Pk',
            defaultMessage: 'Moved {moveCount, plural, one {itinerary item} other {itinerary items}}',
            description: 'Success snackbar after manually moving one or more trip planner itinerary items',
          },
          { moveCount },
        ), 'success')
      } else {
        showSnackbar(intl.formatMessage(
          {
            id: 'd90mGpew2mJSD8IuvHiz',
            defaultMessage: 'Moved {moveCount, plural, one {itinerary item} other {itinerary items}}, but AI context could not sync. Please refresh before continuing.',
            description: 'Critical snackbar after one or more manual itinerary moves succeed but AI conversation context sync fails',
          },
          { moveCount },
        ), 'critical')
      }
      return true
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'niZexKiXSQvQU6UwoSLi',
        defaultMessage: 'Unable to move itinerary item. Please try again.',
        description: 'Error snackbar after manually moving an item to another day in a trip planner itinerary fails',
      }), 'critical')
      return false
    } finally {
      isManualItineraryMovePendingRef.current = false
      setIsManualItineraryMovePending(false)
    }
  }, [isStreaming, isStreamOpen, props.chatId, props.userData.region, currentRegionCode, moveManualItineraryItemsMutation, intl])

  const itineraryValue = useMemo<ItineraryContextValue>(() => ({
    itineraryData: effectiveItineraryData,
    hasItineraryData,
    multiCartId,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    refetchCurrentChatItinerary,
    deleteManualItineraryTarget,
    moveManualItineraryItems,
    isManualItineraryDeletePending,
    isManualItineraryMovePending,
    isFlightCartMutationPending,
    setFlightCartMutationPending,
  }), [
    effectiveItineraryData, hasItineraryData, multiCartId,
    enrichingStopIds, enrichingItemIds,
    scrollTarget, clearScrollTarget,
    refetchCurrentChatItinerary, deleteManualItineraryTarget, moveManualItineraryItems,
    isManualItineraryDeletePending, isManualItineraryMovePending, isFlightCartMutationPending,
  ])

  const filesValue = useMemo<ChatFilesValue>(() => ({
    files,
    setFiles,
  }), [files, setFiles])

  return (
    <ChatActionsContext.Provider value={actionsValue}>
      <ChatStreamStatusContext.Provider value={statusValue}>
        <ChatScrollContext.Provider value={scrollValue}>
          <ChatFilesContext.Provider value={filesValue}>
            <ChatUiActionContext.Provider value={uiActionValue}>
              <ChatStreamMessagesContext.Provider value={streamMessagesValue}>
                <ItineraryContext.Provider value={itineraryValue}>
                  <AddedItineraryItemContext.Provider value={addedItineraryItemValue}>
                    <StaffRewindContext.Provider value={staffRewind}>
                      {children}
                    </StaffRewindContext.Provider>
                  </AddedItineraryItemContext.Provider>
                </ItineraryContext.Provider>
              </ChatStreamMessagesContext.Provider>
            </ChatUiActionContext.Provider>
          </ChatFilesContext.Provider>
        </ChatScrollContext.Provider>
      </ChatStreamStatusContext.Provider>
    </ChatActionsContext.Provider>
  )
}
